import React, { ChangeEvent, useEffect, useState } from 'react'

import Button from 'components_new/atoms/Button'
import Stack from 'components_new/atoms/Stack'
import Text from 'components_new/atoms/Text'
import TextField from 'components_new/atoms/TextField'
import Tooltip from 'components_new/atoms/Tooltip'
import Alert from 'components_new/atoms/Alert'
import Spin from 'components_new/atoms/Spin'
import Icon from 'components_new/atoms/Icon'
import Box from 'components_new/atoms/Box'

import SimpleDialog from 'components_new/molecules/SimpleDialog'
import MFASetupModal from 'components_new/organisms/MFASetupModal'
import { Lang, useTranslation } from 'language/translator'

import { useSelfActions } from 'redux/actions/Self'
import { getMFAStatus, disableMFA } from 'redux/api/MFA'
import SettingsGroup from 'components_new/molecules/SettingsGroup'
import SettingsItem from 'components_new/molecules/SettingsItem'
import Radio from 'components_new/atoms/Radio'
import Divider from 'components_new/atoms/Divider'
import { useAccountSettings } from 'redux/hooks/SignedInAccount'
import SelectCustomer from './SelectCustomer'
import { useCustomer } from 'redux/hooks/Customer'
import TestEnvironmentToggle from 'components_new/organisms/TestEnvironmentToggle'

/**
 * The EditProfileDialog component lets
 * the user edit its profile.
 */
interface EditProfileDialogProps {
  open: boolean
  onClose: () => void
}

const EditProfileDialog = (props: EditProfileDialogProps) => {
  const { open, onClose } = props

  const { tryUpdateSelf } = useSelfActions()

  const { account } = useAccountSettings()
  const customer = useCustomer()

  const { t, lang } = useTranslation()
  const [firstName, setFirstName] = useState<string>(account?.first_name || '')
  const [lastName, setLastName] = useState<string>(account?.last_name || '')
  const [language, setLanguage] = useState<Lang>(lang)

  const [mfaEnabled, setMfaEnabled] = useState(false)
  const [mfaLoading, setMfaLoading] = useState(true)
  const [setupModalOpen, setSetupModalOpen] = useState(false)
  const [mfaError, setMfaError] = useState<string | null>(null)
  const [mfaSuccess, setMfaSuccess] = useState<string | null>(null)
  // null while not disabling; otherwise the code typed so far.
  const [disableCode, setDisableCode] = useState<string | null>(null)

  useEffect(() => {
    if (open) {
      setLanguage(lang)
      loadMFAStatus()
    }
  }, [open])

  const loadMFAStatus = async () => {
    setMfaLoading(true)
    setMfaError(null)

    try {
      const response = await getMFAStatus()

      setMfaEnabled(response.data.data.mfa_enabled)
    } catch (err) {
      setMfaError(t('users', 'mfa_status_load_error'))
    } finally {
      setMfaLoading(false)
    }
  }

  const handleEnableMFA = () => {
    setSetupModalOpen(true)
    setMfaSuccess(null)
  }

  const handleDisableMFA = async () => {
    if (!disableCode || !/^\d{6}$/.test(disableCode)) {
      setMfaError(t('users', 'mfa_code_length_error'))

      return
    }

    setMfaLoading(true)
    setMfaError(null)

    try {
      await disableMFA(disableCode)
      setMfaEnabled(false)
      setDisableCode(null)
      setMfaSuccess(t('users', 'mfa_disabled_success'))
    } catch (err) {
      setMfaError(t('users', 'mfa_invalid_code_error'))
    } finally {
      setMfaLoading(false)
    }
  }

  const handleMFASetupSuccess = () => {
    setMfaEnabled(true)
    setMfaSuccess(t('users', 'mfa_enabled_success'))
    loadMFAStatus()
  }

  return (
    <SimpleDialog
      component="form"
      title={t('global', 'settings')}
      open={open}
      onClose={onClose}
      actions={
        <>
          <Button variant="text" onClick={onClose}>
            {t('global', 'cancel')}
          </Button>
          <Button
            variant="contained"
            onClick={() => {
              tryUpdateSelf({
                data: {
                  attributes: {
                    first_name: firstName,
                    last_name: lastName,
                    language
                  }
                }
              })

              onClose()
            }}
            disabled={firstName.length === 0 || lastName.length === 0}
          >
            {t('global', 'save')}
          </Button>
        </>
      }
    >
      <Text fontWeight="medium" fontSize={16} sx={{ mb: 1 }}>
        {t('global', 'profile')}
      </Text>
      <Stack sx={{ gap: 2 }}>
        <TextField
          label={t('users', 'first_name')}
          onChange={(event: ChangeEvent<HTMLTextAreaElement>) => {
            setFirstName(event.target.value)
          }}
          error={firstName.length === 0}
          helperText={
            firstName.length === 0
              ? t('users', 'first_name_missing_error')
              : null
          }
          value={firstName}
          required
        />
        <TextField
          label={t('users', 'last_name')}
          onChange={(event: ChangeEvent<HTMLTextAreaElement>) => {
            setLastName(event.target.value)
          }}
          error={lastName.length === 0}
          helperText={
            lastName.length === 0 ? t('users', 'last_name_missing_error') : null
          }
          value={lastName}
          required
        />
      </Stack>
      <Tooltip title={t('users', 'email_change_disabled_tooltip')}>
        <TextField
          label={t('users', 'email')}
          disabled={true}
          value={account?.email || ''}
        />
      </Tooltip>
      <SelectCustomer />
      <Divider sx={{ mb: 2, mt: 2 }} />
      <Text fontWeight="medium" fontSize={16} sx={{ mb: 1 }}>
        {t('global', 'language')}
      </Text>
      <SettingsGroup>
        <SettingsItem
          body={
            'Detta val översätter Homepals användargränssnitt, till exempel knappar, menyer, åtgärder och systemmeddelanden, till svenska. Språket som er organisation själv har definierat, såsom namn på mätetal, entiteter och processer, påverkas inte. På så sätt skiljer vi mellan organisationens affärsspråk och gränssnittets språk.'
          }
          icon={
            <Radio
              checked={language === 'sv'}
              onChange={() => setLanguage('sv')}
              size="small"
              sx={{ height: 24, width: 24 }}
            />
          }
          title={'Svenska'}
        />
        <SettingsItem
          body={
            'This setting translates the Homepal user interface, such as buttons, menus, actions, and system messages, into English. The language defined by your organization, such as the names of metrics, entities, and processes, will not be affected. This way, we separate your organization\'s business language from the interface language.'
          }
          icon={
            <Radio
              checked={language === 'en'}
              onChange={() => setLanguage('en')}
              size="small"
              sx={{ height: 24, width: 24 }}
            />
          }
          title={'English'}
        />
      </SettingsGroup>
      <TestEnvironmentToggle />
      <Divider sx={{ mb: 2, mt: 2 }} />
      {customer?.has_idp ? null : (
        <>
          <Text fontWeight="medium" fontSize={16} sx={{ mb: 1 }}>
            {t('users', 'mfa_title')}
          </Text>
          <Text variant="body2" color="text.secondary" sx={{ mb: 2 }}>
            {t('users', 'mfa_description')}
          </Text>

          {mfaError && (
            <Alert
              severity="error"
              sx={{ mb: 2 }}
              onClose={() => setMfaError(null)}
            >
              {mfaError}
            </Alert>
          )}

          {mfaSuccess && (
            <Alert
              severity="success"
              sx={{ mb: 2 }}
              onClose={() => setMfaSuccess(null)}
            >
              {mfaSuccess}
            </Alert>
          )}

          {mfaLoading ? (
            <Box sx={{ display: 'flex', justifyContent: 'center', py: 2 }}>
              <Spin size={24} />
            </Box>
          ) : (
            <Box
              sx={{
                display: 'flex',
                justifyContent: 'space-between',
                alignItems: 'center',
                mb: 2
              }}
            >
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                <Icon
                  name={mfaEnabled ? 'CheckCircle' : 'Cancel'}
                  color={mfaEnabled ? 'success' : 'disabled'}
                />
                <Text variant="body2">
                  {mfaEnabled
                    ? t('users', 'mfa_enabled')
                    : t('users', 'mfa_disabled')}
                </Text>
              </Box>

              {mfaEnabled && disableCode === null && (
                <Button
                  variant="outlined"
                  color="error"
                  onClick={() => {
                    setDisableCode('')
                    setMfaError(null)
                    setMfaSuccess(null)
                  }}
                  disabled={mfaLoading}
                  size="small"
                >
                  {t('users', 'mfa_disable')}
                </Button>
              )}
              {mfaEnabled && disableCode !== null && (
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                  <TextField
                    size="small"
                    label={t('users', 'mfa_verification_code')}
                    value={disableCode}
                    onChange={(e) => setDisableCode(e.target.value)}
                    placeholder="000000"
                    inputProps={{
                      maxLength: 6,
                      pattern: '[0-9]*',
                      inputMode: 'numeric'
                    }}
                    autoFocus
                  />
                  <Button
                    variant="text"
                    onClick={() => setDisableCode(null)}
                    size="small"
                  >
                    {t('users', 'mfa_cancel')}
                  </Button>
                  <Button
                    variant="outlined"
                    color="error"
                    onClick={handleDisableMFA}
                    disabled={mfaLoading}
                    size="small"
                  >
                    {t('users', 'mfa_disable')}
                  </Button>
                </Box>
              )}
              {!mfaEnabled && (
                <Button
                  variant="contained"
                  color="primary"
                  onClick={handleEnableMFA}
                  disabled={mfaLoading}
                  size="small"
                >
                  {t('users', 'mfa_enable')}
                </Button>
              )}
            </Box>
          )}

          <Text variant="body2" color="text.secondary">
            <strong>{t('users', 'mfa_note_label')}</strong>{' '}
            {t('users', 'mfa_note')}
          </Text>

          <MFASetupModal
            open={setupModalOpen}
            onClose={() => setSetupModalOpen(false)}
            onSuccess={handleMFASetupSuccess}
          />
        </>
      )}
    </SimpleDialog>
  )
}

export default EditProfileDialog
